import { useCallback, useRef, useState } from 'react';

/**
 * Per-row "in flight" tracking for actions that share one mutation hook.
 *
 * A TanStack mutation only remembers its latest call, so checking
 * `mutation.variables === rowId` re-enables row A as soon as row B starts.
 * This keeps its own set: `run(key, fn)` refuses to start a second call for
 * a key that's still going, and `has(key)` drives that row's spinner.
 */
export function usePendingKeys(): {
  has: (key: string | number) => boolean;
  run: <T>(key: string | number, fn: () => Promise<T>) => Promise<T | undefined>;
} {
  const [keys, setKeys] = useState<ReadonlySet<string>>(() => new Set());
  // The ref sees a key the instant it starts, before React re-renders.
  const live = useRef(new Set<string>());

  const has = useCallback((key: string | number) => keys.has(String(key)), [keys]);

  const run = useCallback(async <T,>(key: string | number, fn: () => Promise<T>): Promise<T | undefined> => {
    const k = String(key);
    if (live.current.has(k)) return undefined;
    live.current.add(k);
    setKeys(new Set(live.current));
    try {
      return await fn();
    } finally {
      live.current.delete(k);
      setKeys(new Set(live.current));
    }
  }, []);

  return { has, run };
}
